import * as api from "../client";
import type { FirmTab, FirmTabType, ListResponse, StatusUpdatePayload } from "./types";

const BASE = "/v1/profiles/firm-tabs";

/**
 * Canonical staff-ui firm-tab kinds (snake_case, mirrors the API enum and
 * `FirmTabType`). Profiles-api routes use hyphenated slugs — `KIND_TO_SLUG`
 * bridges the two. Only the two staff-authorable kinds remain; General and the
 * SOFTSUPP-466 kinds are retired (their profiles-api endpoints return 404).
 */
export const FIRM_TAB_KINDS = ["client_testimonials", "diversity"] as const;

export type FirmTabKind = (typeof FIRM_TAB_KINDS)[number];

const KIND_TO_SLUG: Record<FirmTabKind, string> = {
  client_testimonials: "client-testimonials",
  diversity: "diversity",
};

const TAB_TYPES: readonly string[] = Object.values(KIND_TO_SLUG);

const SLUG_TO_KIND: Record<string, FirmTabKind> = Object.fromEntries(
  (Object.entries(KIND_TO_SLUG) as [FirmTabKind, string][]).map(([kind, slug]) => [slug, kind]),
);

const SLUG_LIST_KEY: Record<string, string> = {
  "client-testimonials": "firmTabsClientTestimonials",
  diversity: "firmTabsDiversity",
};

const SLUG_ENTITY_KEY: Record<string, string> = {
  "client-testimonials": "firmTabClientTestimonials",
  diversity: "firmTabDiversity",
};

export function kindToSlug(kind: FirmTabKind): string {
  return KIND_TO_SLUG[kind];
}

export function isFirmTabKind(value: string | undefined): value is FirmTabKind {
  return !!value && (FIRM_TAB_KINDS as readonly string[]).includes(value);
}

/**
 * Child-entry input shape for the client testimonials repeater. Mirrors the
 * profiles-api InsertFirmTabTestimonialEntrySchema wire shape as a UI input
 * shape (no server-managed `id`/`tabId`) — the form maps loaded server rows
 * down to it on hydrate and emits it under the wire key `entries`. Kept here
 * (not in shared `types.ts`) because it is firm-tab-form-specific.
 */
export type FirmTabTestimonialEntryInput = {
  clientName: string;
  clientCompany?: string;
  testimonial: string;
  sortOrder?: number;
};

/**
 * UI-only type. The profiles-api diversity controller does NOT currently
 * accept an images array, so the form keeps it in local state and does NOT
 * send it. See the BACKEND GAP comments in `FirmTabForm.tsx`. Remove the
 * "UI-only" caveat once the API persists it.
 */
export type DiversityImage = {
  mediaHash?: string | null;
  mediaFilename?: string | null;
  caption?: string;
};

export type FirmTabFilters = {
  companyId?: string;
  tabType?: FirmTabType;
  /** Per-kind filter — accepts staff-ui kind (snake_case). */
  kind?: FirmTabKind;
  status?: string;
  /** Free-text search; forwarded as `q` when the API supports it. */
  q?: string;
  /**
   * Region filter (§3.4): `regionKind` + `regionId` (integer) together or
   * neither. `regionId` is the integer `api_region_country_v2` id.
   */
  regionKind?: "region" | "country";
  regionId?: string;
  limit?: number;
  offset?: number;
};

function buildQueryString(filters: FirmTabFilters): string {
  const params = new URLSearchParams();
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.status) params.append("status", filters.status);
  if (filters.q) params.append("search", filters.q);
  if (filters.regionKind && filters.regionId) {
    params.append("regionKind", filters.regionKind);
    params.append("regionId", filters.regionId);
  }
  if (filters.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters.offset !== undefined) params.append("offset", String(filters.offset));
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function listOneSlug(slug: string, filters: FirmTabFilters): Promise<ListResponse<FirmTab>> {
  const query = buildQueryString(filters);
  const raw = await api.get<
    Record<string, FirmTab[]> & {
      pagination: { total: number; limit: number; offset: number };
    }
  >(`${BASE}/${slug}${query}`);
  const key = SLUG_LIST_KEY[slug];
  const rawItems = (raw[key] as FirmTab[] | undefined) ?? [];
  // profiles-api per-kind list endpoints don't include `tabType` on rows, but
  // we know the kind from the slug we just requested. Tag each row so the
  // staff-ui list can build correct per-kind edit links.
  const kind = SLUG_TO_KIND[slug];
  const items = kind ? rawItems.map(item => ({ ...item, tabType: kind })) : rawItems;
  return {
    data: items,
    total: raw.pagination?.total ?? items.length,
    limit: raw.pagination?.limit ?? filters.limit ?? 20,
    offset: raw.pagination?.offset ?? filters.offset ?? 0,
  };
}

export const firmTabsService = {
  /**
   * Cross-kind list (default) or per-kind list when `filters.kind` is set.
   * When kind is omitted, fans out across every kind in parallel.
   */
  async list(filters: FirmTabFilters = {}): Promise<ListResponse<FirmTab>> {
    if (filters.kind) {
      return listOneSlug(KIND_TO_SLUG[filters.kind], filters);
    }
    const results = await Promise.allSettled(TAB_TYPES.map(slug => listOneSlug(slug, filters)));
    let totalSum = 0;
    const allItems: FirmTab[] = [];
    results.forEach(result => {
      if (result.status === "fulfilled") {
        allItems.push(...result.value.data);
        totalSum += result.value.total;
      }
    });
    return {
      data: allItems,
      total: totalSum,
      limit: filters.limit ?? 20,
      offset: filters.offset ?? 0,
    };
  },

  /**
   * Fetch-by-id without explicit kind: fan out across the per-kind endpoints
   * in parallel and return the first 200. profiles-api does not expose a
   * single GET /firm-tabs/:id route; only the kind-routed endpoints exist.
   */
  async get(id: string): Promise<FirmTab> {
    const results = await Promise.allSettled(
      (FIRM_TAB_KINDS as readonly FirmTabKind[]).map(k => firmTabsService.getByKind(k, id)),
    );
    for (const r of results) {
      if (r.status === "fulfilled") return r.value;
    }
    throw new Error(`Firm tab ${id} not found in any tab kind`);
  },

  /** Per-kind fetch (uses kind-routed endpoint and unwraps the entity envelope). */
  async getByKind(kind: FirmTabKind, id: string): Promise<FirmTab> {
    const slug = KIND_TO_SLUG[kind];
    const raw = await api.get<Record<string, unknown>>(`${BASE}/${slug}/${encodeURIComponent(id)}`);
    const entityKey = SLUG_ENTITY_KEY[slug];
    return (raw[entityKey] ?? raw) as FirmTab;
  },

  async create(kind: FirmTabKind, payload: Record<string, unknown>): Promise<FirmTab> {
    const slug = KIND_TO_SLUG[kind];
    const raw = await api.post<Record<string, unknown>>(`${BASE}/${slug}`, payload);
    const entityKey = SLUG_ENTITY_KEY[slug];
    return (raw[entityKey] ?? raw) as FirmTab;
  },

  async update(kind: FirmTabKind, id: string, payload: Record<string, unknown>): Promise<FirmTab> {
    const slug = KIND_TO_SLUG[kind];
    const raw = await api.put<Record<string, unknown>>(
      `${BASE}/${slug}/${encodeURIComponent(id)}`,
      payload,
    );
    const entityKey = SLUG_ENTITY_KEY[slug];
    return (raw[entityKey] ?? raw) as FirmTab;
  },

  remove(kind: FirmTabKind, id: string): Promise<void> {
    const slug = KIND_TO_SLUG[kind];
    return api.del(`${BASE}/${slug}/${encodeURIComponent(id)}`);
  },

  /**
   * Patch status without explicit kind: look up the tab via the fan-out get(),
   * then PATCH the kind-routed status endpoint. profiles-api does not expose a
   * single PATCH /firm-tabs/:id/status route.
   */
  async updateStatus(id: string, payload: StatusUpdatePayload): Promise<FirmTab> {
    const tab = await firmTabsService.get(id);
    const slug = KIND_TO_SLUG[tab.tabType as FirmTabKind];
    const raw = await api.patch<Record<string, unknown>>(
      `${BASE}/${slug}/${encodeURIComponent(id)}/status`,
      payload,
    );
    const entityKey = SLUG_ENTITY_KEY[slug];
    return (raw[entityKey] ?? raw) as FirmTab;
  },
};
